import { supabase } from './supabase';

class FeedbackService {
  async submitFeedback(feedbackData) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .insert({
          user_id: feedbackData.userId,
          feedback_type: feedbackData.type,
          rating: feedbackData.rating,
          comment: feedbackData.comment,
          context_data: feedbackData.contextData || {}
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[FeedbackService] Submit feedback error:', error);
      return { success: false, error: error.message };
    }
  }

  async getUserFeedback(userId, limit = 20, offset = 0) {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select(`
          *,
          admin:admin_id(full_name)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[FeedbackService] Get user feedback error:', error);
      return { success: false, error: error.message };
    }
  }

  async getAllFeedback(filters = {}, limit = 50, offset = 0) {
    try {
      let query = supabase
        .from('feedback')
        .select(`
          *,
          user:user_id(full_name, email),
          admin:admin_id(full_name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.type) {
        query = query.eq('feedback_type', filters.type);
      }
      if (filters.rating) {
        query = query.eq('rating', filters.rating);
      }

      const { data, error } = await query.range(offset, offset + limit - 1);

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[FeedbackService] Get all feedback error:', error);
      return { success: false, error: error.message };
    }
  }

  async updateFeedbackStatus(feedbackId, status, adminResponse = null, adminId = null) {
    try {
      const updateData = {
        status,
        updated_at: new Date().toISOString()
      };

      if (adminResponse) {
        updateData.admin_response = adminResponse;
        updateData.admin_id = adminId;
        updateData.responded_at = new Date().toISOString();
      }

      const { data, error } = await supabase
        .from('feedback')
        .update(updateData)
        .eq('id', feedbackId)
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('[FeedbackService] Update feedback error:', error);
      return { success: false, error: error.message };
    }
  }

  async getFeedbackStats() {
    try {
      const { data, error } = await supabase
        .from('feedback')
        .select('feedback_type, rating, status, created_at');

      if (error) throw error;

      // Calculate stats
      const stats = {
        total: data.length,
        byType: {},
        byRating: {},
        byStatus: {},
        averageRating: 0,
        recentCount: 0 // Last 7 days
      };

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      let ratingSum = 0;
      let ratingCount = 0;

      data.forEach(item => {
        // By type
        stats.byType[item.feedback_type] = (stats.byType[item.feedback_type] || 0) + 1;
        
        // By rating
        if (item.rating) {
          stats.byRating[item.rating] = (stats.byRating[item.rating] || 0) + 1;
          ratingSum += item.rating;
          ratingCount++;
        }
        
        // By status
        stats.byStatus[item.status] = (stats.byStatus[item.status] || 0) + 1;
        
        // Recent count
        if (new Date(item.created_at) > sevenDaysAgo) {
          stats.recentCount++;
        }
      });

      stats.averageRating = ratingCount > 0 ? (ratingSum / ratingCount).toFixed(1) : 0;

      return { success: true, data: stats };
    } catch (error) {
      console.error('[FeedbackService] Get feedback stats error:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new FeedbackService();